/**
 * 品牌独立站 API 服务
 */

import { apiRequest } from '@/utils/api'

const BRANDSITE_API_BASE = '/api/brand-sites'

export const brandSiteService = {
  // 获取站点列表
  async getSites(params = {}) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/sites/`, params)
      return {
        success: true,
        data: response.data.results || response.data,
        total: response.data.count || (response.data.results || response.data).length
      }
    } catch (error) {
      console.error('获取站点列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取站点列表失败'
      }
    }
  },

  // 获取单个站点
  async getSite(id) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/sites/${id}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取站点失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取站点失败'
      }
    }
  },

  // 创建站点
  async createSite(siteData) {
    try {
      const response = await apiRequest.post(`${BRANDSITE_API_BASE}/sites/`, siteData)
      return {
        success: true,
        data: response.data,
        message: '站点创建成功'
      }
    } catch (error) {
      console.error('创建站点失败:', error)
      
      // 提取详细的错误信息
      const errorData = error.response?.data || {}
      let errorMessage = '创建站点失败'
      
      // 优先检查字段级别的错误
      if (errorData.custom_domain) {
        errorMessage = Array.isArray(errorData.custom_domain) 
          ? errorData.custom_domain[0] 
          : errorData.custom_domain
      } else if (errorData.name) {
        errorMessage = Array.isArray(errorData.name) 
          ? errorData.name[0] 
          : errorData.name
      } else if (errorData.message) {
        errorMessage = errorData.message
      } else if (errorData.detail) {
        errorMessage = errorData.detail
      }
      
      return {
        success: false,
        message: errorMessage,
        errorData: errorData // 保留完整的错误数据供调用方使用
      }
    }
  },

  // 更新站点
  async updateSite(id, siteData) {
    try {
      const response = await apiRequest.put(`${BRANDSITE_API_BASE}/sites/${id}/`, siteData)
      return {
        success: true,
        data: response.data,
        message: '站点更新成功'
      }
    } catch (error) {
      console.error('更新站点失败:', error)
      
      // 提取详细的错误信息
      const errorData = error.response?.data || {}
      let errorMessage = '更新站点失败'
      
      // 优先检查字段级别的错误（如 custom_domain）
      if (errorData.custom_domain) {
        errorMessage = Array.isArray(errorData.custom_domain) 
          ? errorData.custom_domain[0] 
          : errorData.custom_domain
      } else if (errorData.message) {
        errorMessage = errorData.message
      } else if (errorData.detail) {
        errorMessage = errorData.detail
      }
      
      return {
        success: false,
        message: errorMessage,
        errorData: errorData // 保留完整的错误数据供调用方使用
      }
    }
  },

  // 删除站点
  async deleteSite(id) {
    try {
      await apiRequest.delete(`${BRANDSITE_API_BASE}/sites/${id}/`)
      return {
        success: true,
        message: '站点删除成功'
      }
    } catch (error) {
      console.error('删除站点失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '删除站点失败'
      }
    }
  },

  // 复制站点
  async duplicateSite(id) {
    try {
      const response = await apiRequest.post(`${BRANDSITE_API_BASE}/sites/${id}/duplicate/`)
      return {
        success: true,
        data: response.data,
        message: '站点复制成功'
      }
    } catch (error) {
      console.error('复制站点失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '复制站点失败'
      }
    }
  },

  // 检查域名是否可用
  async checkDomain(id, domain) {
    try {
      const response = await apiRequest.post(`${BRANDSITE_API_BASE}/sites/${id}/check_domain/`, { domain })
      return {
        success: response.data.success || true,
        available: response.data.available,
        message: response.data.message
      }
    } catch (error) {
      console.error('检查域名失败:', error)
      return {
        success: false,
        available: false,
        message: error.response?.data?.message || '检查域名失败'
      }
    }
  },

  // 验证域名
  async verifyDomain(id) {
    try {
      const response = await apiRequest.post(`${BRANDSITE_API_BASE}/sites/${id}/verify_domain/`)
      return {
        success: response.data.success || true,
        message: response.data.message,
        data: response.data
      }
    } catch (error) {
      console.error('验证域名失败:', error)
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || '验证域名失败'
      }
    }
  },

  // 获取域名状态
  async getDomainStatus(id) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/sites/${id}/domain_status/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取域名状态失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取域名状态失败'
      }
    }
  },

  // 申请SSL
  async applySSL(id) {
    try {
      const response = await apiRequest.post(`${BRANDSITE_API_BASE}/sites/${id}/apply_ssl/`)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('申请SSL失败:', error)
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || '申请SSL失败'
      }
    }
  },

  // 一键验证并申请SSL
  async verifyAndSetup(id) {
    try {
      const response = await apiRequest.post(`${BRANDSITE_API_BASE}/sites/${id}/verify_and_setup/`)
      return {
        success: true,
        message: response.data.message
      }
    } catch (error) {
      console.error('一键配置失败:', error)
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || '一键配置失败'
      }
    }
  },

  // 获取模板列表
  async getTemplates(params = {}) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/templates/`, params)
      return {
        success: true,
        data: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取模板列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取模板列表失败'
      }
    }
  },

  // 获取单个模板
  async getTemplate(id) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/templates/${id}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取模板失败'
      }
    }
  },

  // AI生成品牌独立站
  async generateWithAI(params) {
    try {
      // AI生成可能需要较长时间，设置10分钟超时
      const response = await apiRequest.post(
        `${BRANDSITE_API_BASE}/sites/generate_with_ai/`, 
        params,
        { timeout: 600000 }  // 10分钟超时（600秒）
      )
      return {
        success: response.data.success || true,
        site: response.data.site,
        preview_url: response.data.preview_url,
        message: response.data.message || 'AI生成成功'
      }
    } catch (error) {
      console.error('AI生成失败:', error)
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'AI生成失败'
      }
    }
  },

  // 获取站点访问记录（支持分页）
  async getVisits(id, page = 1, pageSize = 20) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/sites/${id}/visits/`, {
        params: {
          page,
          page_size: pageSize
        }
      })
      return {
        success: response.data.success || true,
        data: response.data.data || [],
        total: response.data.total || 0,
        mobile_count: response.data.mobile_count || 0,
        desktop_count: response.data.desktop_count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || pageSize,
        total_pages: response.data.total_pages || 0
      }
    } catch (error) {
      console.error('获取访问记录失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取访问记录失败',
        data: [],
        total: 0,
        mobile_count: 0,
        desktop_count: 0,
        page: 1,
        page_size: pageSize,
        total_pages: 0
      }
    }
  },

  // 获取联系表单提交记录
  async getContacts(id, page = 1, pageSize = 20) {
    try {
      const response = await apiRequest.get(`${BRANDSITE_API_BASE}/sites/${id}/contacts/`, {
        params: {
          page,
          page_size: pageSize
        }
      })
      return {
        success: response.data.success || true,
        data: response.data.data || [],
        total: response.data.total || 0,
        mobile_count: response.data.mobile_count || 0,
        desktop_count: response.data.desktop_count || 0,
        processed_count: response.data.processed_count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || pageSize,
        total_pages: response.data.total_pages || 0
      }
    } catch (error) {
      console.error('获取联系表单记录失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取联系表单记录失败',
        data: [],
        total: 0,
        mobile_count: 0,
        desktop_count: 0,
        processed_count: 0,
        page: 1,
        page_size: pageSize,
        total_pages: 0
      }
    }
  },
}

export default brandSiteService

